import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
    subscribeSnapshotsDelDia,
    getSnapshotActivo,
    tieneSnapshotInicial,
} from '../services/saldosService';
import { subscribeByComitentes } from '../services/comitentesQueryService';
import { calculateSaldos } from '../utils/balanceCalculator';
import { buildSnapshotsDesdeProyeccion } from '../utils/saldosProyeccion';
import { useFechaHoy } from './useFechaHoy';
import { useAcreditacionesEsperadas } from './useAcreditacionesEsperadas';
import { useDebitosLicitaciones } from './useDebitosLicitaciones';
import {
    BUCKET_ARS,
    BUCKET_USD,
    BUCKET_CABLE,
    MONEDA_MOVIMIENTO,
    SALDO_INICIAL_FIELD,
    bucketDeMoneda,
} from '../constants/monedas';

const SaldosClientesContext = createContext(null);

// Mismos filtros que las consultas del modo staff, aplicados en memoria en modo
// productor (sus consultas solo pueden restringir por nroComitente).
const ESTADOS_FINANCIACION_VIVOS = ['Hecha', 'Hecha ', 'En proceso', 'En Proceso', 'Pendiente'];
const ESTADOS_VIVOS = ['Hecha', 'En Proceso', 'Pendiente'];
const TIPOS_INSTRUCCION_DINERO = ['Ingreso', 'Egreso', 'Suscripcion FCI', 'Rescate FCI'];

const emptyEntry = (nroComitente, nombre, tieneSnapshot) => ({
    nroComitente,
    nombre: nombre || '',
    saldoSnapshot: 0,
    saldoReal: 0,
    saldoProyectado: 0,
    movimientosReales: [],
    movimientosPendientes: [],
    tieneSnapshot,
});

// Resta el impacto de un movimiento (caución o pase) del entry pre-calculado para
// mostrar "saldo si este movimiento no existiera" sin recalcular todo el mapa.
// `campo` permite excluir por otra clave: un débito de licitación se excluye por
// `licitacionId`.
const applyExcludeMovimiento = (entry, tipo, id, campo = 'id') => {
    if (!entry || !id) return entry;
    let saldoReal = entry.saldoReal;
    let saldoProyectado = entry.saldoProyectado;
    const movimientosReales = [];
    const movimientosPendientes = [];
    entry.movimientosReales.forEach(m => {
        if (m.tipo === tipo && m[campo] === id) {
            saldoReal -= m.monto;
            saldoProyectado -= m.monto;
        } else {
            movimientosReales.push(m);
        }
    });
    entry.movimientosPendientes.forEach(m => {
        if (m.tipo === tipo && m[campo] === id) {
            saldoProyectado -= m.monto;
        } else {
            movimientosPendientes.push(m);
        }
    });
    return { ...entry, saldoReal, saldoProyectado, movimientosReales, movimientosPendientes };
};

/*
 * Proveedor de saldos por comitente.
 *
 * - Modo staff (comitentesFilter = null): lee las colecciones completas y el
 *   snapshot del custodio, como siempre.
 * - Modo productor (comitentesFilter = ['568000025', ...]): cada consulta se
 *   restringe a esos comitentes (única lectura que las reglas le permiten) y el
 *   snapshot se reconstruye desde `saldosComitente`. El cálculo de saldos es el
 *   mismo, así el productor ve exactamente lo que ve el trader para sus clientes.
 */
export const SaldosClientesProvider = ({ user, clientesDb, comitentesFilter = null, children }) => {
    const [initialSaldos, setInitialSaldos] = useState([]);
    const [allCauciones, setAllCauciones] = useState([]);
    const [allPases, setAllPases] = useState([]);
    const [allOperaciones, setAllOperaciones] = useState([]);
    const [instruccionesDinero, setInstruccionesDinero] = useState([]);
    const [snapshotsHoy, setSnapshotsHoy] = useState([]);
    const [isLoadingSaldos, setIsLoadingSaldos] = useState(true);

    // La suscripción a los snapshots y el cálculo de "movimientos del día" van
    // atados a esta fecha: al cambiar el día se resuscribe solo, sin refrescar.
    const fechaHoy = useFechaHoy();

    const esModoProductor = comitentesFilter !== null && comitentesFilter !== undefined;
    // Clave estable del filtro: evita resuscribirse cuando el padre re-renderiza
    // con un array nuevo pero de igual contenido.
    const comitentesKey = esModoProductor ? [...comitentesFilter].map(String).sort().join('|') : null;
    const comitentesProductor = useMemo(
        () => (comitentesKey === null ? null : (comitentesKey ? comitentesKey.split('|') : [])),
        [comitentesKey],
    );

    useEffect(() => {
        if (!user) {
            setInitialSaldos([]);
            setAllCauciones([]);
            setAllPases([]);
            setAllOperaciones([]);
            setInstruccionesDinero([]);
            setSnapshotsHoy([]);
            return undefined;
        }

        setIsLoadingSaldos(true);
        const unsubs = [];

        if (comitentesKey !== null) {
            const comitentes = comitentesKey ? comitentesKey.split('|') : [];
            const stopLoading = () => setIsLoadingSaldos(false);

            unsubs.push(subscribeByComitentes('saldos', comitentes, (docs) => {
                setInitialSaldos(docs);
                stopLoading();
            }, stopLoading));

            unsubs.push(subscribeByComitentes('cauciones', comitentes, (docs) =>
                setAllCauciones(docs.filter(c => ESTADOS_FINANCIACION_VIVOS.includes(c.estado)))));

            unsubs.push(subscribeByComitentes('pases', comitentes, (docs) =>
                setAllPases(docs.filter(p => ESTADOS_FINANCIACION_VIVOS.includes(p.estado)))));

            unsubs.push(subscribeByComitentes('operaciones', comitentes, (docs) =>
                setAllOperaciones(docs.filter(op => op.plazo === 'C.I.' && ESTADOS_VIVOS.includes(op.estado)))));

            unsubs.push(subscribeByComitentes('instrucciones', comitentes, (docs) =>
                setInstruccionesDinero(docs.filter(i =>
                    TIPOS_INSTRUCCION_DINERO.includes(i.tipo) && ESTADOS_VIVOS.includes(i.estado)))));

            unsubs.push(subscribeByComitentes('saldosComitente', comitentes, (docs) =>
                setSnapshotsHoy(buildSnapshotsDesdeProyeccion(docs, fechaHoy))));

            return () => unsubs.forEach(u => u && u());
        }

        unsubs.push(onSnapshot(
            query(collection(db, 'saldos')),
            (snap) => {
                setInitialSaldos(snap.docs.map(d => d.data()));
                setIsLoadingSaldos(false);
            },
            (error) => { console.error('Error suscribiendo saldos:', error); setIsLoadingSaldos(false); },
        ));

        unsubs.push(onSnapshot(
            query(
                collection(db, 'cauciones'),
                where('estado', 'in', ESTADOS_FINANCIACION_VIVOS),
            ),
            (snap) => setAllCauciones(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (error) => console.error('Error suscribiendo cauciones:', error),
        ));

        unsubs.push(onSnapshot(
            query(
                collection(db, 'pases'),
                where('estado', 'in', ESTADOS_FINANCIACION_VIVOS),
            ),
            (snap) => setAllPases(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (error) => console.error('Error suscribiendo pases:', error),
        ));

        unsubs.push(onSnapshot(
            query(
                collection(db, 'operaciones'),
                where('plazo', '==', 'C.I.'),
                where('estado', 'in', ESTADOS_VIVOS),
            ),
            (snap) => setAllOperaciones(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (error) => console.error('Error suscribiendo operaciones:', error),
        ));

        unsubs.push(onSnapshot(
            query(
                collection(db, 'instrucciones'),
                where('tipo', 'in', TIPOS_INSTRUCCION_DINERO),
                where('estado', 'in', ESTADOS_VIVOS),
            ),
            (snap) => setInstruccionesDinero(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            (error) => console.error('Error suscribiendo instrucciones:', error),
        ));

        unsubs.push(subscribeSnapshotsDelDia(fechaHoy, setSnapshotsHoy));

        return () => unsubs.forEach(u => u && u());
    }, [user, comitentesKey, fechaHoy]);

    const snapshotActivo = useMemo(() => getSnapshotActivo(snapshotsHoy), [snapshotsHoy]);

    // Las acreditaciones esperadas que el operador marca 'Acreditada' entran al saldo
    // como un movimiento ejecutado (ver calculateSaldos).
    const acreditaciones = useAcreditacionesEsperadas({ user, fecha: fechaHoy, comitentes: comitentesProductor });

    // Lo adjudicado en licitaciones se descuenta desde la fecha de caja (ver
    // buildDebitosLicitaciones).
    const debitosLicitaciones = useDebitosLicitaciones({ user, fecha: fechaHoy, comitentes: comitentesProductor });

    const fallbackSnapshot = useMemo(() => {
        if (snapshotActivo) return null;
        if (!initialSaldos.length) return null;
        return {
            tipo: 'inicial',
            uploadedAt: null,
            comitentes: initialSaldos.map(s => ({
                nroComitente: s.nroComitente,
                nombre: s.nombre,
                saldoARS: Number(s[SALDO_INICIAL_FIELD[BUCKET_ARS]]) || 0,
                saldoUSD: Number(s[SALDO_INICIAL_FIELD[BUCKET_USD]]) || 0,
                saldoCable: Number(s[SALDO_INICIAL_FIELD[BUCKET_CABLE]]) || 0,
                titulosA3: s.titulosA3 || [],
            })),
        };
    }, [snapshotActivo, initialSaldos]);

    const snapshotEnUso = snapshotActivo || fallbackSnapshot;
    const tieneSnapshotEnUso = !!snapshotEnUso;

    const saldosPorBucket = useMemo(() => {
        const base = {
            snapshot: snapshotEnUso,
            cauciones: allCauciones,
            pases: allPases,
            operaciones: allOperaciones,
            instrucciones: instruccionesDinero,
            acreditaciones: acreditaciones.porComitente,
            licitaciones: debitosLicitaciones.movimientos,
            clientesDb,
            fechaHoy,
        };
        return {
            [BUCKET_ARS]: calculateSaldos({ ...base, moneda: BUCKET_ARS }),
            [BUCKET_USD]: calculateSaldos({ ...base, moneda: BUCKET_USD }),
            [BUCKET_CABLE]: calculateSaldos({ ...base, moneda: BUCKET_CABLE }),
        };
    }, [
        snapshotEnUso, allCauciones, allPases, allOperaciones, instruccionesDinero,
        acreditaciones.porComitente, debitosLicitaciones.movimientos, clientesDb, fechaHoy,
    ]);

    // En modo productor la proyección solo conserva el snapshot activo: si hay
    // uno cargado hoy, el día ya arrancó con el inicial (la UI de carga exige el
    // inicial antes de cualquier intradiario).
    const tieneSnapshotInicialHoy = useMemo(
        () => (esModoProductor ? snapshotsHoy.length > 0 : tieneSnapshotInicial(snapshotsHoy)),
        [snapshotsHoy, esModoProductor],
    );

    const getSaldoComitente = useCallback((nroComitente, moneda, opts = {}) => {
        if (!nroComitente) return null;
        const map = saldosPorBucket[bucketDeMoneda(moneda)];
        const base = map.get(nroComitente)
            || emptyEntry(nroComitente, clientesDb?.[nroComitente] || '', tieneSnapshotEnUso);
        const withTieneSnapshot = { ...base, tieneSnapshot: tieneSnapshotEnUso };
        if (opts.excludeCaucionId) {
            return applyExcludeMovimiento(withTieneSnapshot, 'caucion', opts.excludeCaucionId);
        }
        if (opts.excludePaseId) {
            return applyExcludeMovimiento(withTieneSnapshot, 'pase', opts.excludePaseId);
        }
        if (opts.excludeInstruccionId) {
            return applyExcludeMovimiento(withTieneSnapshot, 'instruccion', opts.excludeInstruccionId);
        }
        if (opts.excludeLicitacionId) {
            return applyExcludeMovimiento(withTieneSnapshot, 'licitacion', opts.excludeLicitacionId, 'licitacionId');
        }
        return withTieneSnapshot;
    }, [saldosPorBucket, clientesDb, tieneSnapshotEnUso]);

    const clientesDeudoresSinPrecarga = useCallback((moneda) => {
        const bucket = bucketDeMoneda(moneda);
        const map = saldosPorBucket[bucket];
        const monedaCaucion = MONEDA_MOVIMIENTO[bucket];
        // Un cliente con caución o pase cargado hoy en esa moneda ya tiene la
        // deuda cubierta/gestionada: no se lista como deudor sin precarga.
        const comitentesConFinanciacionHoy = new Set([
            ...allCauciones.filter(c => c.moneda === monedaCaucion).map(c => c.nroComitente),
            ...allPases.filter(p => p.moneda === monedaCaucion).map(p => p.nroComitente),
        ]);
        const result = [];
        map.forEach((entry, nroComitente) => {
            if (entry.saldoProyectado < 0 && !comitentesConFinanciacionHoy.has(nroComitente)) {
                result.push({ ...entry, tieneSnapshot: tieneSnapshotEnUso });
            }
        });
        return result.sort((a, b) => a.saldoProyectado - b.saldoProyectado);
    }, [saldosPorBucket, allCauciones, allPases, tieneSnapshotEnUso]);

    const value = useMemo(() => ({
        fechaHoy,
        initialSaldos,
        allCauciones,
        allPases,
        allOperaciones,
        instruccionesDinero,
        acreditaciones,
        debitosLicitaciones,
        snapshotsHoy,
        snapshotActivo,
        snapshotEnUso,
        tieneSnapshotInicialHoy,
        isLoadingSaldos,
        saldosPorBucket,
        getSaldoComitente,
        clientesDeudoresSinPrecarga,
        esModoProductor,
        comitentesProductor,
    }), [
        fechaHoy, initialSaldos, allCauciones, allPases, allOperaciones, instruccionesDinero,
        acreditaciones, debitosLicitaciones, snapshotsHoy, snapshotActivo, snapshotEnUso, tieneSnapshotInicialHoy,
        isLoadingSaldos, saldosPorBucket,
        getSaldoComitente, clientesDeudoresSinPrecarga, esModoProductor, comitentesProductor,
    ]);

    return (
        <SaldosClientesContext.Provider value={value}>
            {children}
        </SaldosClientesContext.Provider>
    );
};

export const useSaldosClientes = () => {
    const ctx = useContext(SaldosClientesContext);
    if (!ctx) {
        throw new Error('useSaldosClientes debe usarse dentro de <SaldosClientesProvider>');
    }
    return ctx;
};
